import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { AssemblyWithChildren, normalizeDescription, sortQuoteRequestLines } from "shared";

export type QuoteRequestLineReferenceSearchResult = {
  visibleLineIds: Set<string>;
  forcedExpandedAssemblyIds: Set<string>;
};

const lineMatchesReference = (line: QuoteRequestLineDto, normalizedSearchText: string): boolean =>
  normalizeDescription(line.part.name).includes(normalizedSearchText);

const revealSubtree = (
  assembly: AssemblyWithChildren<QuoteRequestLineDto>,
  visibleLineIds: Set<string>,
  forcedExpandedAssemblyIds: Set<string>,
): void => {
  visibleLineIds.add(assembly.quoteRequestLine.id);
  if (assembly.children.length === 0) {
    return;
  }
  forcedExpandedAssemblyIds.add(assembly.quoteRequestLine.id);
  assembly.children.forEach((child) =>
    revealSubtree(child, visibleLineIds, forcedExpandedAssemblyIds),
  );
};

const markVisibleMatches = (
  assembly: AssemblyWithChildren<QuoteRequestLineDto>,
  normalizedSearchText: string,
  visibleLineIds: Set<string>,
  forcedExpandedAssemblyIds: Set<string>,
): boolean => {
  if (lineMatchesReference(assembly.quoteRequestLine, normalizedSearchText)) {
    revealSubtree(assembly, visibleLineIds, forcedExpandedAssemblyIds);
    return true;
  }

  const hasVisibleChild = assembly.children
    .map((child) =>
      markVisibleMatches(child, normalizedSearchText, visibleLineIds, forcedExpandedAssemblyIds),
    )
    .some(Boolean);

  if (!hasVisibleChild) {
    return false;
  }

  visibleLineIds.add(assembly.quoteRequestLine.id);
  forcedExpandedAssemblyIds.add(assembly.quoteRequestLine.id);
  return true;
};

export const searchQuoteRequestLinesByReference = (
  quoteRequestLines: QuoteRequestLineDto[],
  searchText: string,
): QuoteRequestLineReferenceSearchResult => {
  const normalizedSearchText = normalizeDescription(searchText);
  const visibleLineIds = new Set<string>();
  const forcedExpandedAssemblyIds = new Set<string>();

  sortQuoteRequestLines(quoteRequestLines).assemblies.forEach((root) =>
    markVisibleMatches(root, normalizedSearchText, visibleLineIds, forcedExpandedAssemblyIds),
  );

  return { visibleLineIds, forcedExpandedAssemblyIds };
};
